import {
  Box,
  Chip,
  Grid,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { createTagBadge } from "../../components/discovery/finding-editor";
import MaterialQuoteView from "../../components/discovery/material-quote-view";
import { InsightDetails } from "../../components/insights/insight-details";
import { InsightPageTableSkeleton, InsightTableSkeleton } from "../../components/insights/insight-skeleton";
import { baseAtom } from "../../stores/jotai/base";
import { selectedInsightRowAtom } from "../../stores/jotai/insights/selectedInsightRow";
import { useGetFindingTable } from "../../tanstackQuery/queries/findings/useGetFindingTable";
import { priorityBadge } from "../../util/insights/handle-export";
import InsightTable from "./insight-table";
import { MRT_RowVirtualizer } from "material-react-table";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { useGetArtifacts } from "../../tanstackQuery/queries/artifacts";

export const renderRatingColumn = (rating) => {
  const values = priorityBadge(rating);
  const icon: any = values?.icon;
  return rating > 0 ? (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{
        backgroundColor: "white",
        maxWidth: "90px",
        minWidth: "85px",
        padding: "5px 6px",
        border: "1px solid #c2c2c2",
        borderRadius: "4px",
        fontSize: "15px",
        color: "#000000",
        height: "28px"
      }}
    >
      <img
        src={icon?.src}
        alt={values?.label}
        style={{
          width: "14px",
          height: "14px",
          marginRight: "5px"
        }}
      />
      {values?.label}
    </Stack>
  ) : null;
};

export const renderFindingQuote = (finding, quote) => {
  let isVultr = false;
  let isRemirror = false;

  try {
    const p = JSON.parse(quote);

    if (p.textCache !== undefined) {
      isRemirror = true;
    }

    if (p.anchor !== undefined) {
      isVultr = true;
    }
  } catch (e) {}

  if (isRemirror) {
    return <Typography noWrap>{JSON.parse(quote)?.textCache}</Typography>;
  }

  if (isVultr) {
    return (
      <MaterialQuoteView
        artifactID={finding?.artifactID}
        selection={JSON.parse(quote)}
      />
    );
  }

  return <Typography noWrap>{quote}</Typography>;
};

export const renderFindingTypes = (tags, theme) => {
  return (
    <>
        {tags?.map(
          (tag) => tag?.label && createTagBadge(theme, tag, () => {}, undefined)
        )}
    </>
  );
};

export const breakoutLinksColumns = (links) => {
  return [
    links?.map((link) => {
      const entryData = link?.category?.entry?.entry;
      const abbreviationData = link?.category?.abbreviation;

      const leader =
        link?.supports === 0 ||
        link?.supports === "0" ||
        link?.supports === false
          ? "Doesn't support "
          : "Supports ";
      const tootltipMessage = `${leader}${entryData}`;

      return (
        entryData &&
        abbreviationData && (
          <Tooltip
          key={"tooltip-insight-table-breakout-link-" + link.linkID}
          title={tootltipMessage}
        >
          <Chip
            label={abbreviationData}
            variant="outlined"
            sx={{ borderRadius: "4px", height: "28px", fontSize: "15px" }}
          />
        </Tooltip>
        )
      );
    })
  ];
};

export default function InsightPage() {
  const [selectedInsightRow, setSelectedInsightRow] = useAtom(
    selectedInsightRowAtom
  );
  const [matchedInsightGroup, setMatchedInsightGroup] = useState<any>(undefined);
  const [discoverySession] = useAtom(discoverySessionAtom);

  const theme = useTheme();
  const base = useAtomValue(baseAtom);
  const { keycloak } = useKeycloak();
  const { pathname } = useLocation();
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  const [filters, setFilters] = useState({
    change: false,
    allTags: true,
    unTaggedInsights: true
  });

  const { data: tableData, isLoading:isTableDataLoading } = useGetFindingTable(
    { accessToken: keycloak?.token },
    {
      groupID: base?.team?.groupID, isMetaInsightEnabled : true,
    },
    { enabled: !!base?.team?.groupID }
  );

  const { data: artifactDetails, isLoading: artifactLoading } = useGetArtifacts(
    { accessToken: keycloak?.token },
    { groupID: base?.team?.groupID, sessionID:discoverySession?.sessionID },
    {
      enabled: !!base?.team?.groupID
    }
  );
  
  if (pathname === "/") {
    return (
      <div>
        {!isTableDataLoading && !artifactLoading ? (
          <InsightTable
            tableData={tableData}
            rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
            artifactDetails={artifactDetails}
            setMatchedInsightGroup={setMatchedInsightGroup}
          />
        ) : (
          <InsightTableSkeleton />
        )} 
      </div>
    );
  }

  const filteredData = useMemo(() => {
    if (!tableData) return null;

    if (!(filters.allTags || filters.unTaggedInsights)) {
      setSelectedInsightRow(null);
      return [];
    }

    if (filters.allTags && filters.unTaggedInsights) {
      return tableData;
    }

    return tableData.filter((item) => {
      const hasAnyTags = item.tags.length > 0;

      if (filters.allTags) {
        return hasAnyTags;
      }

      if (filters.unTaggedInsights) {
        return !hasAnyTags;
      }

      return true;
    });
  }, [filters, tableData]);

  useEffect(() => {
    if (
      filters.change &&
      filteredData?.length > 0 &&
      selectedInsightRow !== null &&
      pathname !== "/"
    ) {
      setSelectedInsightRow(filteredData[0]);
      if (rowVirtualizerInstanceRef?.current) {
        rowVirtualizerInstanceRef.current.scrollToIndex(0);
      }
    }
  }, [filters]);

  return (
   <Box sx={{ margin: "0px 12px 5px 11px" }}>
     <Grid container={true} spacing={1} >
      <Grid item md={9.6}>
        {!filteredData && !artifactLoading ? (
          <InsightPageTableSkeleton />
        ) : (
          <InsightTable
            tableData={filteredData}
            rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
            artifactDetails={artifactDetails}
            setMatchedInsightGroup={setMatchedInsightGroup}
          />
        )}
      </Grid>

      <Grid item md={2.4}>
        <InsightDetails
          insight={selectedInsightRow}
          filters={filters}
          setFilters={setFilters}
          matchedInsightGroup={matchedInsightGroup}
          setMatchedInsightGroup={setMatchedInsightGroup}
        />
      </Grid>
    </Grid>
   </Box>
  );
}
